import { useEffect, useRef } from 'react'
import type { Ordinances } from '@fs/zion-tree-types'
import { useGroupsContext } from '@fs/zion-tree-groups'
import useEffectiveUserId from '../hooks/useEffectiveUserId'
import { type SwrCustomizedResponse, useSwrRequest } from './useSwrRequest'
import { readOrdinances } from './emulators/ordinancesBE'
import useFocusPerson, { mutateFocusPerson } from './useFocusPerson'
import { useShowTempleContext } from '../providers/ShowTempleContext'

function anyOrdinanceStatusIsCalculating(ordinances?: Ordinances): boolean {
  if (!ordinances) return false
  return ordinances.ordinances.some(({ status }) => status === 'Calculating')
}

export const swrKey = (personId: string, cpUserId: string, treeId: string): string =>
  `/service/tree/tree-data/reservations/person/${personId}/ordinances?pendingTransfer=true&owner=${cpUserId}&treeId=${treeId}&enforceTemplePolicyEx=true`

export default function useOrdinances(): SwrCustomizedResponse<Ordinances, 'ordinances'> {
  const { focusPerson } = useFocusPerson()
  const { effectiveUserId } = useEffectiveUserId()
  const { currentTreeId } = useGroupsContext()
  const [showTemple] = useShowTempleContext()
  const wasCalculatingRef = useRef(false)
  const personId = focusPerson?.id
  const treeId = currentTreeId || 'PRIVATE'

  const {
    data: ordinances,
    error,
    isLoading,
    isValidating,
    mutate: mutateOrdinances,
  } = useSwrRequest<Ordinances>(
    effectiveUserId && personId && showTemple ? swrKey(personId, effectiveUserId, treeId) : null,
    undefined,
    {
      emulator: readOrdinances,
    }
  )

  // We need to refetch the focus person once the ordinances finish calculating.
  useEffect(() => {
    if (!wasCalculatingRef.current && anyOrdinanceStatusIsCalculating(ordinances)) {
      wasCalculatingRef.current = true
    } else if (wasCalculatingRef.current && !anyOrdinanceStatusIsCalculating(ordinances) && personId) {
      wasCalculatingRef.current = false
      mutateFocusPerson(personId, currentTreeId || 'PRIVATE')
    }
  }, [personId, ordinances, currentTreeId])

  return { ordinances, error, isLoading, isValidating, mutateOrdinances }
}
